import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  InputAdornment,
  Tooltip,
  Chip,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import proxy from '../../api/axios';
import {
  ACCENT,
  AdminCard,
  AdminEmpty,
  AdminLoading,
  AdminPage,
  AdminPageHeader,
  AdminStatGrid,
  AdminToolbar,
  MetaChip,
  useAdminPageTheme,
} from './adminUi';

const ManageNeetTests = () => {
  const { border, muted, isDark } = useAdminPageTheme();
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [takingTestId, setTakingTestId] = useState(null);
  const [addQuestionsTarget, setAddQuestionsTarget] = useState(null);
  const [addQuestionsJson, setAddQuestionsJson] = useState('');
  const [addQuestionsError, setAddQuestionsError] = useState('');
  const [addingQuestions, setAddingQuestions] = useState(false);

  const fetchTests = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await proxy.get('/neet-tests/admin');
      setTests(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load NEET tests');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTests();
  }, [fetchTests]);

  const stats = useMemo(() => {
    const active = tests.filter((t) => t.isActive).length;
    const totalAttempts = tests.reduce((sum, t) => sum + (t.attemptCount || 0), 0);
    return { total: tests.length, active, inactive: tests.length - active, totalAttempts };
  }, [tests]);

  const filteredTests = useMemo(
    () => tests.filter((t) => t.title?.toLowerCase().includes(searchQuery.toLowerCase())),
    [tests, searchQuery],
  );

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await proxy.delete(`/neet-tests/admin/${deleteTarget._id}`);
      toast.success('Test deleted');
      setDeleteTarget(null);
      fetchTests();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete test');
      console.error(error);
    }
  };

  const handleTakeTest = async (test) => {
    // Fullscreen needs a user gesture — request it synchronously on the click.
    document.documentElement.requestFullscreen?.().catch(() => {});
    setTakingTestId(test._id);
    try {
      await proxy.post(`/neet-tests/${test._id}/admin-take`);
      navigate(`/neet-tests/${test._id}/live`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not enter test');
    } finally {
      setTakingTestId(null);
    }
  };

  const openAddQuestions = (test) => {
    setAddQuestionsTarget(test);
    setAddQuestionsJson('');
    setAddQuestionsError('');
  };

  const handleAddQuestions = async () => {
    let questions;
    try {
      const parsed = JSON.parse(addQuestionsJson);
      questions = Array.isArray(parsed) ? parsed : parsed.questions;
      if (!Array.isArray(questions) || questions.length === 0) {
        setAddQuestionsError('Paste a JSON array of question objects (or { "questions": [...] }).');
        return;
      }
    } catch (error) {
      setAddQuestionsError('Invalid JSON: ' + error.message);
      return;
    }

    setAddingQuestions(true);
    setAddQuestionsError('');
    try {
      await proxy.post(`/neet-tests/admin/${addQuestionsTarget._id}/questions`, { questions });
      toast.success(`Added ${questions.length} question${questions.length === 1 ? '' : 's'}`);
      setAddQuestionsTarget(null);
      fetchTests();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to add questions';
      setAddQuestionsError(message);
    } finally {
      setAddingQuestions(false);
    }
  };

  const dialogSx = {
    '& .MuiDialog-paper': {
      borderRadius: 2.5,
      border: `1px solid ${border}`,
    },
  };

  return (
    <AdminPage>
      <Helmet>
        <title>Manage NEET Tests | Admin | CrafterHack</title>
      </Helmet>

      <AdminPageHeader
        title="Manage NEET tests"
        subtitle="Create timed NEET-style mock tests from a JSON question bank"
        actions={
          <>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchTests} sx={{ border: `1px solid ${border}` }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => navigate('/admin/neet-tests/new')}
              sx={{ textTransform: 'none', fontWeight: 800, bgcolor: ACCENT, boxShadow: 'none' }}
            >
              Create test
            </Button>
          </>
        }
      />

      <AdminStatGrid
        items={[
          { label: 'TOTAL TESTS', value: stats.total },
          { label: 'ACTIVE', value: stats.active, color: '#1a7f37' },
          { label: 'INACTIVE', value: stats.inactive, color: '#8b949e' },
          { label: 'TOTAL ATTEMPTS', value: stats.totalAttempts, color: '#2563eb' },
        ]}
      />

      <AdminToolbar>
        <TextField
          size="small"
          placeholder="Search tests…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: muted }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220, flex: 1 }}
        />
      </AdminToolbar>

      {loading ? (
        <AdminLoading />
      ) : filteredTests.length === 0 ? (
        <AdminEmpty
          title="No NEET tests found"
          description="Create a new test by uploading or pasting a JSON question bank."
        />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {filteredTests.map((test) => (
            <AdminCard
              key={test._id}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 0.75 }}>
                  <MetaChip
                    label={test.isActive ? 'Active' : 'Inactive'}
                    color={test.isActive ? '#1a7f37' : '#8b949e'}
                    isDark={isDark}
                  />
                  <MetaChip label={`${test.questionCount} questions`} isDark={isDark} />
                  <MetaChip label={`${test.attemptCount} attempts`} color="#2563eb" isDark={isDark} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                  <FactCheckOutlinedIcon sx={{ fontSize: 18, color: ACCENT }} />
                  <Typography sx={{ fontWeight: 800, fontSize: '0.98rem' }}>{test.title}</Typography>
                  <Chip
                    size="small"
                    label={`${test.totalMarks} marks`}
                    sx={{ height: 20, fontWeight: 700, fontSize: '0.65rem' }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: muted, fontSize: '0.82rem', mt: 0.35 }}>
                  {test.durationMinutes} minutes
                  {test.startTime && test.endTime
                    ? ` · Open ${new Date(test.startTime).toLocaleString()} → ${new Date(test.endTime).toLocaleString()}`
                    : ' · Schedule not set'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Take test (QA — doesn't count on the leaderboard)">
                  <span>
                    <IconButton
                      size="small"
                      disabled={takingTestId === test._id}
                      onClick={() => handleTakeTest(test)}
                    >
                      <PlayCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Add questions (JSON)">
                  <IconButton size="small" onClick={() => openAddQuestions(test)}>
                    <PlaylistAddOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="View attempts">
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/admin/neet-tests/${test._id}/attempts`)}
                  >
                    <BarChartOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/admin/neet-tests/${test._id}/edit`)}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={() => setDeleteTarget(test)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </AdminCard>
          ))}
        </Box>
      )}

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} sx={dialogSx}>
        <DialogTitle sx={{ fontWeight: 800 }}>Delete test?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteTarget ? `“${deleteTarget.title}” will be removed permanently. Existing attempt records are kept.` : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 800, boxShadow: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(addQuestionsTarget)} onClose={() => !addingQuestions && setAddQuestionsTarget(null)} maxWidth="sm" fullWidth sx={dialogSx}>
        <DialogTitle sx={{ fontWeight: 800 }}>Add questions</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            {addQuestionsTarget
              ? `Paste a JSON array of new questions to append to "${addQuestionsTarget.title}" (${addQuestionsTarget.questionCount} existing). Same shape as the question objects in the test-creation JSON.`
              : ''}
          </DialogContentText>
          <TextField
            multiline
            fullWidth
            minRows={10}
            maxRows={20}
            placeholder={`[\n  {\n    "type": "mcq",\n    "subject": "Physics",\n    "questionText": "What is the SI unit of force?",\n    "options": ["Joule", "Newton", "Watt", "Pascal"],\n    "correctOptionIndex": 1\n  }\n]`}
            value={addQuestionsJson}
            onChange={(e) => {
              setAddQuestionsJson(e.target.value);
              if (addQuestionsError) setAddQuestionsError('');
            }}
            sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.82rem' } }}
          />
          {addQuestionsError && (
            <Typography variant="body2" sx={{ color: '#cf222e', mt: 1, whiteSpace: 'pre-wrap' }}>
              {addQuestionsError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setAddQuestionsTarget(null)} disabled={addingQuestions} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddQuestions}
            disabled={addingQuestions || !addQuestionsJson.trim()}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 800, bgcolor: ACCENT, boxShadow: 'none', '&:hover': { bgcolor: '#0f5c5e' } }}
          >
            {addingQuestions ? 'Adding…' : 'Add questions'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminPage>
  );
};

export default ManageNeetTests;
